import { useEffect, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import trainingHtml from './gwi360-training-dispatching-v1.html?raw';

/** Which module this route serves. Each is permissioned separately — not every
 *  dispatcher uses Smart Preplanning — so the modules never chain into each
 *  other; the sidebar links are the entry points. */
export type DispatchingModule = 'boards' | 'ai' | 'optimization';

const MODULE_NUMBER: Record<DispatchingModule, number> = {
  boards: 4,
  ai: 5,
  optimization: 6,
};

const TITLES: Record<DispatchingModule, string> = {
  boards: 'Working the Boards',
  ai: 'AI-Assisted Dispatch',
  optimization: 'Optimization & Visibility',
};

interface DispatchingTrainingProps {
  module?: DispatchingModule;
}

function DispatchingTraining({ module = 'boards' }: DispatchingTrainingProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { accounts } = useMsal();

  // The simulation runs inside an iframe from a blob URL, so it has no access to
  // the app's MSAL context. Inject the signed-in name (and the module) into the
  // raw HTML before building the blob rather than prompting the user for it.
  const traineeName = accounts[0]?.name || accounts[0]?.username || 'Trainee';

  const html = useMemo(() => {
    // Escape for a JS single-quoted string literal.
    const safeName = traineeName.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    return trainingHtml
      .replace('__TRAINEE_NAME__', safeName)
      .replace('__START_MODULE__', String(MODULE_NUMBER[module]));
  }, [traineeName, module]);

  useEffect(() => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
    return () => URL.revokeObjectURL(url);
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      title={`Dispatching Training — ${TITLES[module]}`}
      style={{ width: '100%', height: '100%', border: 'none' }}
    />
  );
}

export default DispatchingTraining;
